import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import DataTable from "react-data-table-component";
import {
  Box,
  Stack,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  LinearProgress,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  SettingsOutlined,
  ViewListOutlined as SubJobIcon,
} from "@mui/icons-material";
import ReplayIcon from "@mui/icons-material/Replay";
import { t } from "i18next";
import useAPI from "hooks/useAPI";
import useAlert from "hooks/useAlert";
import useConstants from "constants";
import { usePermissions } from "context/PermissionsContext";
import AlertMessage from "components/UI/AlertMessage";
import WithPermission from "components/WithPermission";
import SortIcon from "components/SortIcon";
import MBDrawer from "components/MBDrawer";
import PageSettings from "components/PageSettings";
import { formatDateWithFormat, formatDateWithFormatForJobData } from "utils/utilities";

const SEARCH_DEBOUNCE_MS = 400;

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_META = {
  completed: { color: "#22c55e", bg: "rgba(34,197,94,0.10)", label: "Completed" },
  failed: { color: "#ef4444", bg: "rgba(239,68,68,0.10)", label: "Failed" },
  running: { color: "#3b82f6", bg: "rgba(59,130,246,0.10)", label: "Running" },
  pending: { color: "#f59e0b", bg: "rgba(245,158,11,0.10)", label: "Pending" },
  cancelled: { color: "#6b7280", bg: "rgba(107,114,128,0.10)", label: "Cancelled" },
  processing: { color: "#8b5cf6", bg: "rgba(139,92,246,0.10)", label: "Processing" },
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { color: "#9ca3af", bg: "rgba(0,0,0,0.06)", label: status };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.color}44`,
        fontWeight: 700,
        fontSize: "0.7rem",
        height: 22,
      }}
    />
  );
};

StatusChip.propTypes = { status: PropTypes.string };

const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// ─── Expanded row ─────────────────────────────────────────────────────────────

const ExpandedRow = ({ data, dateTimeFormat }) => {
  const formatPayload = (payload) => {
    if (!payload) return "No payload available";
    try {
      const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
      let postJobData = parsed?.post_job_data
        ? JSON.parse(JSON.stringify(parsed.post_job_data))
        : {};

      if (data.name === "pull_irrigation_logs" && postJobData) {
        postJobData = formatDateWithFormatForJobData(postJobData, dateTimeFormat);
      }

      if (postJobData && Object.keys(postJobData).length > 0) {
        return JSON.stringify(postJobData, null, 2);
      }
      let apiData = parsed?.api_data ? JSON.parse(JSON.stringify(parsed.api_data)) : {};
      if ((data.name === "pull_controller_data" || data.name === "pull_valves_data") && apiData) {
        apiData = formatDateWithFormatForJobData(apiData, dateTimeFormat);
      }
      return JSON.stringify(apiData, null, 2);
    } catch {
      return typeof payload === "string" ? payload : JSON.stringify(payload, null, 2);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          background: "rgba(0,0,0,0.02)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {t("lang.details") || "Payload Details"}
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            backgroundColor: "background.paper",
            borderRadius: 1.5,
            overflow: "auto",
            fontSize: "0.8rem",
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: "400px",
            border: "1px solid",
            borderColor: "divider",
            color: "text.primary",
          }}
        >
          {formatPayload(data.raw_payload)}
        </Box>
      </Paper>
    </Box>
  );
};

ExpandedRow.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    raw_payload: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  dateTimeFormat: PropTypes.string.isRequired,
};

// ─── Main component ───────────────────────────────────────────────────────────

const SubJobs = ({ id }) => {
  const { api } = useAPI();
  const { errorState, showAlert } = useAlert();
  const { per_page, items_per_page_options, date_time_format } = useConstants();
  const { hasPermission } = usePermissions();

  const table_id = "sub-jobs";
  const default_sort_column = "id";
  const default_sort_direction = "desc";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoaded, setSearchLoaded] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(per_page);
  const [searchInput, setSearchInput] = useState("");
  const [orderColumn, setOrderColumn] = useState(default_sort_column);
  const [orderDirection, setOrderDirection] = useState(default_sort_direction);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [retryingJobId, setRetryingJobId] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Settings drawer
  const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);
  const [tableId, setTableId] = useState(null);
  const [settingsSaveBtnLoading, setSettingsSaveBtnLoading] = useState(false);
  const [defaultSortColumnFromSettings, setDefaultSortColumnFromSettings] =
    useState(default_sort_column);
  const [defaultSortDirectionFromSettings, setDefaultSortDirectionFromSettings] =
    useState(default_sort_direction);
  const [defaultItemsPerPageFromSettings, setDefaultItemsPerPageFromSettings] = useState(per_page);

  // ── Polling helpers ────────────────────────────────────────────────────────
  const hasActiveJobs = () =>
    data.some((row) => row.status !== "completed" && row.status !== "failed");

  const getActiveJobIds = () =>
    data
      .filter((row) => row.status !== "completed" && row.status !== "failed")
      .map((row) => row.id);

  const refreshActiveJobs = async () => {
    const activeJobIds = getActiveJobIds();
    if (activeJobIds.length === 0) return;
    try {
      const response = await api.post(`/refresh-sub-jobs/${id}/`, { sub_job_ids: activeJobIds });
      if (response.status === 200) {
        const updatedJobs = response.data.data;
        setData((prevData) => {
          const prevDataIds = new Set(prevData.map((row) => row.id));
          const updatedData = prevData.map((row) => {
            const updatedJob = updatedJobs.find((job) => job.id === row.id);
            return updatedJob ? { ...row, ...updatedJob } : row;
          });
          const newRows = updatedJobs.filter((job) => !prevDataIds.has(job.id));
          return [...updatedData, ...newRows];
        });
      }
    } catch {
      // Silent — don't surface polling errors to the user
    }
  };

  useEffect(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    if (hasActiveJobs() && !loading) {
      const interval = setInterval(() => {
        refreshActiveJobs();
      }, 3000);
      setPollingInterval(interval);
    }
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [data, loading]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchSubJobs = async (sortCol, sortDir, pageSize, isReset, searchValue, nextPage) => {
    setLoading(true);
    let requestConfig = {};

    if (!isInitialLoad && !isReset) {
      requestConfig = {
        params: {
          page: nextPage || page,
          per_page: pageSize || perPage,
          search: searchValue,
          order_col: sortCol || orderColumn,
          order_dir: sortDir || orderDirection,
        },
      };
    }

    try {
      const response = await api.get(`/sub-jobs/${id}/`, requestConfig);
      const result = response.data;
      const ps = result.page_settings;
      setData(result.data || []);
      setTotalRows(result.recordsTotal || 0);

      if (isInitialLoad || isReset) {
        if (ps?.sortColumn) {
          setOrderColumn(ps.sortColumn);
          setDefaultSortColumnFromSettings(ps.sortColumn);
        }
        if (ps?.sortDirection) {
          setOrderDirection(ps.sortDirection);
          setDefaultSortDirectionFromSettings(ps.sortDirection);
        }
        if (ps?.itemsPerPage) {
          setPerPage(ps.itemsPerPage);
          setDefaultItemsPerPageFromSettings(ps.itemsPerPage);
        }
        if (isReset && (!ps || Object.keys(ps).length === 0)) {
          setOrderColumn(default_sort_column);
          setOrderDirection(default_sort_direction);
          setPerPage(per_page);
          setDefaultSortColumnFromSettings(default_sort_column);
          setDefaultSortDirectionFromSettings(default_sort_direction);
          setDefaultItemsPerPageFromSettings(per_page);
        }
        setIsInitialLoad(false);
      }
      setSearchLoaded(true);
    } catch (err) {
      showAlert("error", err?.response?.data?.error || t("lang.something_went_wrong"));
    } finally {
      setLoading(false);
    }
  };

  const isFirstSearchEffect = useRef(true);
  const suppressDebounceOnce = useRef(false);

  useEffect(() => {
    fetchSubJobs(orderColumn, orderDirection, perPage, true, "", 1);
  }, []);

  useEffect(() => {
    if (suppressDebounceOnce.current) {
      suppressDebounceOnce.current = false;
      return;
    }
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      fetchSubJobs(orderColumn, orderDirection, perPage, false, searchInput, 1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handlePageChange = (p) => {
    setPage(p);
    fetchSubJobs(orderColumn, orderDirection, perPage, false, searchInput, p);
  };

  const handlePerPageChange = (pp) => {
    setPerPage(pp);
    setPage(1);
    fetchSubJobs(orderColumn, orderDirection, pp, false, searchInput, 1);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSort = (col, dir) => {
    const nc = col.columnKey;
    const nd = nc === orderColumn ? (orderDirection === "asc" ? "desc" : "asc") : dir;
    setOrderColumn(nc);
    setOrderDirection(nd);
    fetchSubJobs(nc, nd, perPage, false, searchInput, page);
  };

  const handleReset = () => {
    if (searchInput.trim() !== "") suppressDebounceOnce.current = true;
    setSearchInput("");
    fetchSubJobs(orderColumn, orderDirection, perPage, true, "", 1);
  };

  const handleRetryJob = async (subJobId) => {
    setRetryingJobId(subJobId);
    try {
      const response = await api.get(`/retry-sub-job/${subJobId}/`);
      if (response.status === 200) {
        showAlert("success", response.data.message);
        setData((prevData) =>
          prevData.map((item) =>
            item.id === subJobId ? { ...item, status: response.data.job_status } : item
          )
        );
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || t("lang.something_went_wrong"));
    } finally {
      setRetryingJobId(null);
    }
  };

  const getSortIcon = (key) => (
    <SortIcon orderDirection={orderDirection} isActive={orderColumn === key} />
  );

  const handleSettingsClick = (tid) => {
    setTableId(tid);
    setOpenSettingsDrawer(true);
  };

  const handleSettingsChange = async (settings) => {
    const ns = settings[tableId];
    setSettingsSaveBtnLoading(true);
    try {
      const res = await api.post("/user-page-settings/", { page_slug: "sub-jobs", settings });
      if (res.status === 200) {
        setOrderColumn(ns.sortColumn);
        setOrderDirection(ns.sortDirection);
        setPerPage(ns.itemsPerPage);
        setDefaultSortColumnFromSettings(ns.sortColumn);
        setDefaultSortDirectionFromSettings(ns.sortDirection);
        setDefaultItemsPerPageFromSettings(ns.itemsPerPage);
        setPage(1);
        fetchSubJobs(ns.sortColumn, ns.sortDirection, ns.itemsPerPage, false, searchInput, 1);
        setOpenSettingsDrawer(false);
        setTableId(null);
        showAlert("success", res.data.message);
      } else {
        showAlert("error", res.data.error);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || "");
    } finally {
      setSettingsSaveBtnLoading(false);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const colHeader = (label, key) => (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      sx={{ textTransform: "uppercase", fontSize: "0.72rem", fontWeight: 700 }}
    >
      {label}
      {getSortIcon(key)}
    </Stack>
  );

  const columns = [
    {
      name: colHeader(t("lang.hash_id") || "#ID", "id"),
      columnKey: "id",
      selector: (row) => (
        <Stack spacing={0.3}>
          <Typography variant="body2" fontWeight={700} color="text.primary" lineHeight={1.2}>
            #{row.id}
          </Typography>
          {row.bwh_job_id ? (
            <Tooltip title={row.bwh_job_id} placement="right" arrow>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,
                  px: 0.75,
                  py: 0.2,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                  width: "fit-content",
                  cursor: "default",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.65rem",
                    color: "text.secondary",
                    letterSpacing: 0,
                    lineHeight: 1,
                  }}
                >
                  {row.bwh_job_id.slice(0, 8)}&hellip;
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            <Typography
              variant="caption"
              sx={{ fontSize: "0.62rem", color: "text.disabled", fontStyle: "italic" }}
            >
              no bwh id
            </Typography>
          )}
        </Stack>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: colHeader(t("lang.name") || "Name", "name"),
      columnKey: "name",
      selector: (row, index) => (
        <Tooltip title={row.name} placement="top" arrow>
          <Typography variant="caption" fontWeight={600} color="text.primary">
            {row.name != row.job_name
              ? toTitleCase(row.name)
              : toTitleCase(row.name) + " - " + (parseInt(index) + 1)}
          </Typography>
        </Tooltip>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: colHeader(t("lang.details") || "Details", "payload"),
      columnKey: "payload",
      selector: (row) => (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            py: 0.5,
          }}
        >
          {row.payload}
        </Typography>
      ),
      sortable: true,
      grow: 1,
    },
    {
      name: colHeader(t("lang.date") || "Date", "created_at"),
      columnKey: "created_at",
      selector: (row) => (
        <Typography variant="caption" color="text.primary" fontWeight={500}>
          {formatDateWithFormat(row.created_at, date_time_format)}
        </Typography>
      ),
      sortable: true,
      width: "170px",
    },
    {
      name: colHeader(t("lang.status") || "Status", "status"),
      columnKey: "status",
      selector: (row) => (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <StatusChip status={row.status} />
          {row.status === "failed" && row.bwh_job_id && (
            <>
              {retryingJobId === row.id ? (
                <CircularProgress size={14} color="error" />
              ) : (
                <WithPermission permission="can_view_gwm">
                  <Tooltip
                    title={row.can_retry ? t("lang.retry") : t("lang.cannot_retry")}
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={() => row.can_retry && handleRetryJob(row.id)}
                      sx={{ p: 0.25 }}
                    >
                      <ReplayIcon fontSize="small" color={row.can_retry ? "error" : "disabled"} />
                    </IconButton>
                  </Tooltip>
                </WithPermission>
              )}
            </>
          )}
        </Stack>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: colHeader(t("lang.error") || "Error", "error"),
      columnKey: "error",
      selector: (row) => (
        <Typography
          variant="caption"
          color={row.error ? "error" : "text.disabled"}
          sx={{ wordBreak: "break-word", py: 0.5 }}
        >
          {row.error || "—"}
        </Typography>
      ),
      sortable: true,
      wrap: true,
      grow: 1,
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "rgba(0,0,0,0.025)",
        borderBottomWidth: "2px",
        borderBottomColor: "rgba(0,0,0,0.08)",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "8px",
        fontWeight: 700,
        fontSize: "0.72rem",
        color: "#64748b",
        letterSpacing: "0.04em",
      },
    },
    rows: {
      style: {
        minHeight: "52px",
        borderBottomColor: "rgba(0,0,0,0.05) !important",
        "&:hover": { backgroundColor: "rgba(59,130,246,0.04)" },
      },
    },
    cells: {
      style: { paddingLeft: "12px", paddingRight: "8px" },
    },
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <SubJobIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} lineHeight={1}>
              {t("lang.sub_jobs") || "Sub Jobs"}
            </Typography>
            {searchLoaded && (
              <Typography variant="caption" color="text.secondary">
                {totalRows} {totalRows !== 1 ? "records" : "record"}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title={t("lang.settings") || "Settings"} arrow>
            <IconButton
              onClick={() => handleSettingsClick(table_id)}
              sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 2, p: 0.75 }}
            >
              <SettingsOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("lang.reset") || "Refresh"} arrow>
            <IconButton
              onClick={handleReset}
              sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 2, p: 0.75 }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            size="small"
            placeholder={t("lang.actions.search") || "Search…"}
            value={searchInput}
            onChange={handleSearchChange}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.875rem" },
            }}
          />
        </Stack>
      </Stack>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1.5px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <DataTable
          id={table_id}
          className={table_id}
          key={`${orderColumn}-${orderDirection}-${perPage}-${data.length}`}
          columns={columns}
          data={data}
          progressPending={loading}
          progressComponent={
            <Box sx={{ width: "100%", py: 4 }}>
              <LinearProgress sx={{ mx: 3, borderRadius: 3, mb: 2 }} />
              {[...Array(4)].map((_, i) => (
                <Stack key={i} direction="row" gap={2} px={3} py={0.75}>
                  <Skeleton width={60} height={20} />
                  <Skeleton width="40%" height={20} />
                  <Skeleton width={120} height={20} />
                  <Skeleton width={80} height={20} />
                </Stack>
              ))}
            </Box>
          }
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerPageChange}
          onChangePage={handlePageChange}
          onSort={handleSort}
          sortServer
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={items_per_page_options}
          paginationDefaultPage={page}
          clearSelectedRows
          responsive
          selectableRows={false}
          pointerOnHover
          fixedHeader
          persistTableHead
          expandableRows
          expandableRowsComponent={({ data: rowData }) => (
            <ExpandedRow data={rowData} dateTimeFormat={date_time_format} />
          )}
          expandableRowExpanded={() => data.length === 1}
          sortIcon={() => <></>}
          customStyles={customStyles}
          highlightOnHover
          noDataComponent={
            <Box sx={{ py: 8, textAlign: "center" }}>
              <SubJobIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">
                {t("lang.no_records_found") || "No records found."}
              </Typography>
            </Box>
          }
        />
      </Paper>

      <AlertMessage {...errorState} />

      {/* Settings drawer */}
      <MBDrawer
        open={openSettingsDrawer}
        onClose={() => setOpenSettingsDrawer(false)}
        title={t("lang.settings") || "Settings"}
        anchor="right"
        height="100vh"
        width="400px"
      >
        <PageSettings
          saveBtnLoading={settingsSaveBtnLoading}
          currentSortColumn={orderColumn}
          currentSortDirection={orderDirection}
          currentItemsPerPage={perPage}
          tableId={tableId}
          defaultSortColumn={defaultSortColumnFromSettings}
          defaultSortDirection={defaultSortDirectionFromSettings}
          defaultItemsPerPage={defaultItemsPerPageFromSettings}
          sortableColumns={[
            { value: "id", label: t("lang.id") || "ID" },
            { value: "name", label: t("lang.name") || "Name" },
            { value: "status", label: t("lang.status") || "Status" },
            { value: "created_at", label: t("lang.date") || "Date" },
          ]}
          onSettingsChange={handleSettingsChange}
        />
      </MBDrawer>
    </Box>
  );
};

SubJobs.propTypes = {
  id: PropTypes.string.isRequired,
};

export default SubJobs;
